import { isUndefined } from 'lodash';

import { isFunction } from '../guards/isFunction';
import { assertUnreachable } from '../guards/assertUnreachable';

export type LocalStorageNamespace = string;
export type LocalStorageScopeKey = string;
export type LocalStorageKey = `${LocalStorageNamespace}:${string}`;
export type LocalStorageType = 'global' | 'scoped';
export type LocalStorageValue<VALUE> = VALUE | undefined;
export type LocalStorageValueGetter<VALUE> = () => LocalStorageValue<VALUE>;
export type LocalStorageValueSetter<VALUE> = (
   value: LocalStorageValue<VALUE> | ((value: LocalStorageValue<VALUE>) => LocalStorageValue<VALUE>)
) => void;
export type LocalStorageSetterRegisterCallback<VALUE> = (setter: LocalStorageValueSetter<VALUE>) => VoidFunction;

export type LocalStorage<VALUE> = {
   getValue: LocalStorageValueGetter<VALUE>;
   setValue: LocalStorageValueSetter<VALUE>;
   registerSetter: LocalStorageSetterRegisterCallback<VALUE>;
};

const resolveLocalStorageValue = <VALUE>(
   // eslint-disable-next-line @typescript-eslint/no-magic-numbers
   nextSetter: Parameters<LocalStorageValueSetter<VALUE>>[0],
   currentValue: LocalStorageValue<VALUE>
): LocalStorageValue<VALUE> => {
   if (isFunction(nextSetter)) {
      const setter = nextSetter as (previousValue?: LocalStorageValue<VALUE>) => LocalStorageValue<VALUE>;
      return nextSetter.length ? setter(currentValue) : setter();
   }

   return nextSetter;
};

const { getLocalStorageFromCache, setLocalStorageIntoCache } = (() => {
   const STORAGE_CACHE = new Map<string, LocalStorage<any>>();

   const getLocalStorageFromCache = <VALUE>(key: LocalStorageKey): LocalStorage<VALUE> | undefined => {
      if (STORAGE_CACHE.has(key)) {
         return STORAGE_CACHE.get(key) as LocalStorage<VALUE>;
      }

      return undefined;
   };

   const setLocalStorageIntoCache = <VALUE>(key: LocalStorageKey, localStorage: LocalStorage<VALUE>): void => {
      STORAGE_CACHE.set(key, localStorage);
   };

   return {
      getLocalStorageFromCache: getLocalStorageFromCache,
      setLocalStorageIntoCache: setLocalStorageIntoCache,
   };
})();

export const { getLocalStorageScopeKey, setLocalStorageScopeKey } = (() => {
   // eslint-disable-next-line no-undef-init
   let SCOPED_KEY: LocalStorageScopeKey | undefined = undefined;

   const setLocalStorageScopeKey = (scopeKey: LocalStorageScopeKey): void => {
      SCOPED_KEY = scopeKey;
   };

   const getLocalStorageScopeKey = (): LocalStorageScopeKey => {
      if (!SCOPED_KEY) {
         throw new Error('Inizialize local storage scoped key during initializing the application.');
      }

      return SCOPED_KEY;
   };

   return {
      setLocalStorageScopeKey: setLocalStorageScopeKey,
      getLocalStorageScopeKey: getLocalStorageScopeKey,
   };
})();

const { createLocalStorageKey } = (() => {
   const createLocalStorageKey = (key: LocalStorageKey, type: LocalStorageType): LocalStorageKey => {
      switch (type) {
         case 'global':
            return key;
         case 'scoped':
            return `${getLocalStorageScopeKey()}:${key}` as const;
         default:
            return assertUnreachable(type);
      }
   };

   return {
      createLocalStorageKey: createLocalStorageKey,
   };
})();

export const createLocalStorage = <VALUE>(
   key: LocalStorageKey,
   type: LocalStorageType = 'global'
): LocalStorage<VALUE> => {
   const cachedLocalStorage = getLocalStorageFromCache<VALUE>(key);
   if (cachedLocalStorage) {
      return cachedLocalStorage;
   }

   const newLocalStorage = ((): LocalStorage<VALUE> => {
      let setters: LocalStorageValueSetter<VALUE>[] = [];

      const getValue: LocalStorageValueGetter<VALUE> = () => {
         const localStorageKey = createLocalStorageKey(key, type);
         const item = window.localStorage.getItem(localStorageKey);
         // eslint-disable-next-line prettier/prettier
         return item ? JSON.parse(item) as LocalStorageValue<VALUE> : undefined;
      };

      const setValue: LocalStorageValueSetter<VALUE> = (setter) => {
         const localStorageKey = createLocalStorageKey(key, type);
         const nextValue = resolveLocalStorageValue(setter, getValue());
         if (isUndefined(nextValue)) {
            window.localStorage.removeItem(localStorageKey);
         } else {
            window.localStorage.setItem(localStorageKey, JSON.stringify(nextValue));
         }

         setters.forEach((setter) => setter(nextValue));
      };

      const registerSetter: LocalStorageSetterRegisterCallback<VALUE> = (newSetter) => {
         if (!setters.includes(newSetter)) {
            setters.push(newSetter);
         }

         return () => {
            setters = setters.filter((setter) => setter !== newSetter);
         };
      };

      return {
         getValue: getValue,
         registerSetter: registerSetter,
         setValue: setValue,
      };
   })();

   setLocalStorageIntoCache(key, newLocalStorage);
   return newLocalStorage;
};
